import { DateTimeObj, Do, Maybe } from '../../../../universal'
import { React, _ } from '../../../lib'
import { reactFlyout } from '../../component-react'
import { NewSystemFlyoutInstance } from '../../flyouts'
import { svrtsRequest } from '../../svrts-interface'
import { FormButton, FormButtonSet } from '../buttons'
import { FormComponent } from '../form'
import { ListGrid } from '../list-grid'
import { LoadingSpinnerLarge } from '../loading'
import { RSInstanceD, useRSInstance } from '../meta-types'
import { TabControl } from '../tabs'
import { NoteAttachment } from './attachments'
import { FormFieldNoteBody, FormFieldNoteTitle } from './edit'
import { FormFieldNoteTags, getFlattenedTags } from './tags'
import { NoteInfo, ReducerState } from './types'

export const quickTidyNotes = <T extends Record<string, unknown>>(
	rs: ReducerState<T>,
	notes: NoteInfo<T>[],
) => {
	// We're going to auto-run this, so just populate the given notes for selection
	reactFlyout(
		'Tidy Notes',
		[1024, 600],
		<TidyNotes
			rs={rs}
			availableNotes={notes}
			defaultSelectedNotes={notes.map(x => x.ID)}
			autoStart={true}
		/>,
	)
}

export const startTidyingNote = <T extends Record<string, unknown>>(
	rs: ReducerState<T>,
) => {
	// Get all available notes
	// Ensure we only work with active notes
	const availableNotes = rs.props.notes.filter(x => !x.Deleted && !x.Archived)

	// Get the ones to pre-select by default
	// Based on selected notes. If none are selected, selected all
	const selectedNotes = Do(() => {
		if (rs.state.selectedNotes.length == 0) {
			return _.map(availableNotes, x => x.ID)
		}
		return rs.state.selectedNotes
	})

	// Open the flyout component
	reactFlyout(
		'Tidy Notes',
		[1024, 600],
		<TidyNotes
			rs={rs}
			availableNotes={availableNotes}
			defaultSelectedNotes={selectedNotes}
			autoStart={false}
		/>,
	)
}

type TidyRSProps<T extends Record<string, unknown>> = {
	availableNotes: NoteInfo<T>[]
	defaultSelectedNotes: string[]
	autoStart: boolean
	flyout?: NewSystemFlyoutInstance
}
type TidyRSState = {
	selectedNotes: string[]
	isLoading: boolean
	errorMsg: Maybe<string>
	currentApprovalNote: Maybe<string>
	selectedApprovalTab: 'old-text' | 'attachment'
	editedNewNote: Maybe<{
		title: string
		tags: string[]
		note: string
	}>
	resolvedResults: string[]
	results: Maybe<
		{
			ID: string
			Title: string
			Note: string
			Tags: string[]
		}[]
	>
}
type TidyRS<T extends Record<string, unknown>> = RSInstanceD<
	TidyRSProps<T>,
	TidyRSState,
	Record<string, never>,
	Record<string, never>
>

/** Wrapper component for handling the note tidying */
const TidyNotes = <T extends Record<string, unknown>>(props: {
	rs: ReducerState<T>
	availableNotes: NoteInfo<T>[]
	defaultSelectedNotes: string[]
	autoStart: boolean
	flyout?: NewSystemFlyoutInstance
}) => {
	// Unpack
	const rs = props.rs

	// Index the notes by the ID
	const notes = _(props.availableNotes)
		.keyBy(x => x.ID)
		.value()

	// State
	const tidyRS: TidyRS<T> = useRSInstance({
		props: {
			availableNotes: props.availableNotes,
			defaultSelectedNotes: props.defaultSelectedNotes,
			autoStart: props.autoStart,
			flyout: props.flyout,
		},
		defaultState: React.useCallback(
			(p): TidyRSState => ({
				selectedNotes: p.defaultSelectedNotes,
				isLoading: false,
				errorMsg: '',
				results: null,
				currentApprovalNote: null,
				selectedApprovalTab: 'old-text',
				editedNewNote: null,
				resolvedResults: [],
			}),
			[],
		),
		refs: {},
		actionToDelta: null,
	})

	// Action when starting the scan
	const onScan = () => {
		console.log('Starting the note scan')
		tidyRS.updateState({
			isLoading: true,
			errorMsg: null,
			results: null,
		})
		svrtsRequest({
			data: {
				funcID: [0, 62],
				// Define the notes we want analysed
				notes: _(tidyRS.state.selectedNotes)
					.map(x => {
						const note = notes[x]
						if (!note) {
							return null
						}
						return {
							ID: note.ID,
							Title: note.Title || null,
							Note: note.Note || null,
							Attachment: note.Attachment?.Checksum ?? null,
							Tags: note.Tags ?? [],
						}
					})
					.compact()
					.value(),
				// Define the assignable tags the LLM can pick from
				tags: _(getFlattenedTags(rs))
					.sortBy(x => x.DisplayName)
					.filter(x => !(x.NonEditable ?? false))
					.map(x => ({
						ID: x.ID,
						DisplayName: x.DisplayName,
						Description: x.Description,
					}))
					.value(),
				// Any additional instructions
				summaryContextPrompt: rs.props.summaryContextPrompt ?? null,
			},
			any: () => {
				tidyRS.updateState({ isLoading: false })
			},
			no: data => {
				tidyRS.updateState({ errorMsg: data.message })
			},
			yes: data => {
				const newNote = data.results[0]
				tidyRS.updateState({
					results: data.results,
					currentApprovalNote: newNote?.ID ?? null,
					editedNewNote: newNote
						? {
								title: newNote.Title ?? '',
								note: newNote.Note ?? '',
								tags: newNote.Tags ?? [],
							}
						: null,
				})
			},
		})
	}

	// If autoStart is true, then do it now
	React.useEffect(() => {
		if (props.autoStart) {
			onScan()
		}
	}, [])

	// Render
	const wrap = (children: React.ReactNode) => (
		<div className="tailwind-wrapper" style={{ height: '100%' }}>
			{children}
		</div>
	)
	if (tidyRS.state.results != null) {
		return wrap(<TidyNoteApproval rsNotes={rs} rsTidy={tidyRS} />)
	}
	if (tidyRS.props.autoStart) {
		return wrap(<LoadingSpinnerLarge />)
	}
	return wrap(<TidyNoteSelection rsTidy={tidyRS} onScanStart={onScan} />)
}

/** Has the list of notes to pick from. Skipped if autoStart is true */
const TidyNoteSelection = <T extends Record<string, unknown>>(props: {
	rsTidy: TidyRS<T>
	onScanStart: () => void
}) => {
	const rsTidy = props.rsTidy
	return (
		<>
			<div className="pb-2 text-left">
				Select which notes you want to tidy. The system will analyse the content
				of the note (including any attachment) and suggest a better
				title/description for you to approve, translate any non-English content,
				and add any missing tags.
			</div>
			<div className="h-[calc(100%-120px)]">
				<ListGrid
					data={_.values(rsTidy.props.availableNotes)}
					pk={x => x.ID}
					value={rsTidy.state.selectedNotes}
					onUpdate={v => {
						rsTidy.updateState({ selectedNotes: v })
					}}
					checkboxSelection={true}
					multiple={true}
					multipleDefault={true}
					readOnly={rsTidy.state.isLoading}
					defaultSortingKey="edited"
					defaultSortingReverse={true}
					fields={[
						{
							key: 'edited',
							lbl: 'Edited',
							width: { init: 150 },
							text: x =>
								DateTimeObj.parse(x.LastEdited).fmtDMY({
									seconds: false,
									shortYear: true,
									weekday: true,
								}),
							sortVal: x => x.LastEdited,
						},
						{
							key: 'name',
							lbl: 'Note',
							text: x => x.Title || x.Note || '[no content]',
						},
						{
							key: 'progress',
							lbl: 'Status',
							width: { init: 100 },
							text: x => {
								if (rsTidy.state.results != null) {
									return 'Completed'
								}
								if (
									rsTidy.state.isLoading &&
									rsTidy.state.selectedNotes.includes(x.ID)
								) {
									return 'Scanning'
								}
								if (
									rsTidy.state.isLoading &&
									!rsTidy.state.selectedNotes.includes(x.ID)
								) {
									return ''
								}
								if (rsTidy.state.selectedNotes.includes(x.ID)) {
									return 'Selected'
								}
								return ''
							},
						},
					]}
				/>
			</div>
			<FormButtonSet loading={rsTidy.state.isLoading} msg={rsTidy.state.errorMsg}>
				<FormButton
					lbl="Scan Notes"
					type="submit"
					onClick={() => {
						props.onScanStart()
					}}
				/>
				<FormButton
					lbl="Cancel"
					onClick={() => {
						rsTidy.props.flyout?.Close()
					}}
				/>
			</FormButtonSet>
		</>
	)
}

/** The approval UI for the tidied note suggestions from the LLM */
const TidyNoteApproval = <T extends Record<string, unknown>>(props: {
	rsNotes: ReducerState<T>
	rsTidy: TidyRS<T>
}) => {
	const rsTidy = props.rsTidy

	// Get information about the current note we're approving
	const currentNote = rsTidy.state.currentApprovalNote
		? rsTidy.props.availableNotes.find(x => x.ID == rsTidy.state.currentApprovalNote)
		: null

	// Helper function to move to the next note
	const nextNote = () => {
		rsTidy.updateState(s => {
			const resolved = [...s.resolvedResults, s.currentApprovalNote]
			const newNote = s.results.filter(x => !resolved.includes(x.ID))[0]
			if (!newNote) {
				console.log('Closing', rsTidy.props.flyout)
				rsTidy.props.flyout?.Close()
			}
			return {
				resolvedResults: resolved,
				currentApprovalNote: newNote?.ID ?? null,
				editedNewNote: newNote
					? {
							title: newNote.Title ?? '',
							note: newNote.Note ?? '',
							tags: newNote.Tags ?? [],
						}
					: null,
			}
		})
	}

	// Helper function to get the default value for the form - the original note
	const getDefault = () => ({
		title: currentNote?.Title ?? '',
		note: currentNote?.Note ?? '',
		tags: currentNote?.Tags ?? [],
	})

	// If there is nothing selected and nothing available to select, then close the flyout
	React.useEffect(() => {
		const results = rsTidy.state.results
		const resolved = rsTidy.state.resolvedResults
		const unresolved = results?.filter(x => !resolved.includes(x.ID))
		console.log({ unresolved })
		if (unresolved == null || unresolved.length == 0) {
			console.log('Closing', rsTidy.props.flyout)
			rsTidy.props.flyout?.Close()
		}
	}, [rsTidy, currentNote?.ID ?? -1])

	// Render
	return (
		<div className="h-full">
			<div className="flex h-[calc(100%-72px)]">
				<div className="flex-1 max-w-1/2">
					<TabControl
						value={rsTidy.state.selectedApprovalTab}
						onUpdate={v => {
							rsTidy.updateState({ selectedApprovalTab: v })
						}}
						tabs={[
							{
								value: 'old-text',
								label: 'Text',
								content: () => (
									<FormComponent
										key={props.rsTidy.state.currentApprovalNote}
										fields={{
											title: FormFieldNoteTitle(),
											tags: FormFieldNoteTags(props.rsNotes),
											note: FormFieldNoteBody(props.rsNotes, 16),
										}}
										formPrompt="Editing a note"
										lblWidth={40}
										disabled={true}
										defaultValue={getDefault}
										excludeButtons={true}
										disableFormFill={true}
										value={null}
										onUpdate={() => {}}
										onSave={() => {}}
									/>
								),
							},
							{
								value: 'attachment',
								label: 'Attachment',
								content: () => (
									<NoteAttachment
										attachment={currentNote?.Attachment ?? null}
									/>
								),
							},
						]}
					/>
				</div>
				<div className="flex-1 pt-8">
					<FormComponent
						key={props.rsTidy.state.currentApprovalNote}
						fields={{
							title: FormFieldNoteTitle(),
							tags: FormFieldNoteTags(props.rsNotes),
							note: FormFieldNoteBody(props.rsNotes, 16),
						}}
						formPrompt="Editing a note"
						lblWidth={40}
						defaultValue={getDefault}
						value={rsTidy.state.editedNewNote}
						onUpdate={v => {
							rsTidy.updateState({ editedNewNote: v })
						}}
						excludeButtons={true}
						disableFormFill={true}
						onSave={() => {}}
					/>
				</div>
			</div>
			<FormButtonSet loading={rsTidy.state.isLoading} msg={rsTidy.state.errorMsg}>
				<FormButton
					lbl="Save"
					onClick={() => {
						rsTidy.updateState({ isLoading: true })
						makeNoteChanges({
							rsNotes: props.rsNotes,
							rsTidy: rsTidy,
							noteID: currentNote?.ID ?? '',
							noteOld: {
								title: currentNote?.Title ?? '',
								bodyText: currentNote?.Note ?? '',
								tags: currentNote?.Tags ?? [],
							},
							newNote: {
								title: rsTidy.state.editedNewNote?.title ?? '',
								bodyText: rsTidy.state.editedNewNote?.note ?? '',
								tags: rsTidy.state.editedNewNote?.tags ?? [],
							},
							onComplete: successOrError => {
								rsTidy.updateState({ isLoading: false })
								if (successOrError === true) {
									nextNote()
								} else {
									rsTidy.updateState({ errorMsg: successOrError })
								}
							},
						})
					}}
				/>
				<FormButton
					lbl="Skip"
					type="standard"
					onClick={() => {
						nextNote()
					}}
				/>
			</FormButtonSet>
		</div>
	)
}

const makeNoteChanges = async <T extends Record<string, unknown>>(input: {
	rsNotes: ReducerState<T>
	rsTidy: TidyRS<T>
	noteID: string
	noteOld: {
		title: string
		bodyText: string
		tags: string[]
	}
	newNote: {
		title: string
		bodyText: string
		tags: string[]
	}
	onComplete: (successOrError: true | string) => void
}) => {
	// Promise to update the note
	const updateNote = () => {
		return new Promise<true | string>(resolve => {
			input.rsNotes.props.editing?.onEdit({
				noteID: input.noteID,
				changes: {
					title: input.newNote.title,
					note: input.newNote.bodyText,
				},
				onComplete: successOrError => {
					resolve(successOrError)
				},
			})
		})
	}

	// Promise to update the tags
	const updateTags = () => {
		const additions = input.newNote.tags.filter(x => !input.noteOld.tags.includes(x))
		const deletions = input.noteOld.tags.filter(x => !input.newNote.tags.includes(x))

		return new Promise<true | string>(resolve => {
			input.rsNotes.props.tagging?.onUpdate({
				additions: additions.map(x => ({
					noteID: input.noteID,
					tagID: x,
				})),
				deletions: deletions.map(x => ({
					noteID: input.noteID,
					tagID: x,
				})),
				onComplete: successOrError => {
					resolve(successOrError)
				},
			})
		})
	}

	// First update the note, then the tags
	const result = await updateNote()
	if (result !== true) {
		input.onComplete(result)
		return
	}
	input.onComplete(await updateTags())
}
